import React from 'react';
import {bindActionCreators} from 'redux';
import { connect } from 'react-redux';
import * as Actions from '../../../redux/actions';
import 'materialize-css';
import TextareaAutosize from 'react-textarea-autosize';
import '../../../styles/report.scss';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import PdfAttachment, { PdfPreviewFrame } from '../PdfAttachment';
import { isPdfAttachment } from '../../../utils/encodeImageFile';


class EditReportNote extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      confirm: null, // {kind: 'delete' | 'overwrite', text, note}
      previousOpen: false,
    };
  }

  buildScaffold(node) {
    let breadcrumbs = node.breadcrumbs.split('.');
    let reportId = breadcrumbs.reverse().pop();
    let reportScaffold = {
      id: reportId,
      ...breadcrumbs.reduce((val, key) => ({[key]: val}), {...node, synced: false})
    };

    return reportScaffold;
  }

  persist(node, storage, onComplete) {
    if (this.props.saveNode) {
      return this.props.saveNode(node, storage, onComplete);
    }

    return this.props.updateReport(
      this.buildScaffold(node),
      storage,
      onComplete
    );
  }

  saveAndClose(event) {
    event?.stopPropagation();

    this.persist(
      {...this.props.node},
      {fridge: true, freezer: this.props.authToken},
      null
    );

    this.props.close();
  }

  handleInputChange(event) {
    let noteObj = {
      ...this.props.node,
      content: event.target.value,
      synced: false,
    };

    this.persist(
      noteObj,
      {fridge: true},
      this.props.switchFocus(noteObj)
    )
  }

  // Enter still commits the note, but Shift+Enter now falls through to the
  // textarea so multi-line notes are possible.
  handleKeyDown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.saveAndClose(event);
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.saveAndClose(event);
    }
  }

  requestDelete() {
    const node = this.props.node;
    let subject = 'note';

    if (node.content?.length > 0 && node.image?.length > 0) {
      subject = 'note and attachment';
    } else if (node.image?.length > 0) {
      subject = 'attachment';
    }

    this.setState({confirm: {
      kind: 'delete',
      text: `Delete this ${subject}?`,
    }});
  }

  deleteNote() {
    this.persist(
      {...this.props.node, deleted: true},
      {fridge: true, freezer: this.props.authToken},
      null
    );

    this.props.close();
  }

  // Reusing a previous note is destructive when something is already typed,
  // so confirm before clobbering it.
  requestPreviousNote(note) {
    const current = this.props.node.content?.trim();

    if (current?.length > 0 && current !== note.content?.trim()) {
      return this.setState({confirm: {
        kind: 'overwrite',
        text: 'Replace the current note with this one?',
        note: note,
      }});
    }

    this.applyPreviousNote(note);
  }

  applyPreviousNote(note) {
    this.setState({confirm: null, previousOpen: false});

    const noteObj = {
      ...this.props.node,
      content: note.content,
      synced: false,
    };

    this.persist(
      noteObj,
      {fridge: true},
      this.props.switchFocus(noteObj)
    )
  }

  sortedPreviousNotes() {
    return [...(this.props.component?.previous_notes || [])]
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }

  contextLabel() {
    if (`${this.props.component?.id}` === '-1') return 'Service notebook';
    return this.props.component?.name || null;
  }

  renderPreviousNotes(previousNotes) {
    const open = this.state.previousOpen;

    return (
      <div className={`note-editor-drawer${open ? ' is-open' : ''}`} aria-hidden={!open}>
        <div className="note-editor-drawer-inner">
          <div className="note-editor-drawer-head">
            <span className="note-editor-drawer-title">Previous notes</span>
            <span className="note-editor-drawer-hint">Select a note to reuse it</span>
          </div>

          <div className="note-editor-drawer-list">
            {previousNotes.map(note => (
              <button
                type="button"
                key={note.id}
                className="note-editor-previous"
                onClick={() => this.requestPreviousNote(note)}
              >
                <span className="note-editor-previous-meta">
                  {note?.date || 'Unknown date'} · {note?.author || 'Unknown author'}
                </span>
                <span className="note-editor-previous-content">
                  {note.content?.replace(/<[^>]*>/g, '')}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  renderConfirm() {
    const confirm = this.state.confirm;
    const destructive = confirm.kind === 'delete';

    return (
      <div className="note-editor-confirm">
        <div className="note-editor-confirm-sheet">
          <span className="note-editor-confirm-text">{confirm.text}</span>

          {confirm.kind === 'overwrite' ? (
            <span className="note-editor-confirm-preview">
              {confirm.note.content?.replace(/<[^>]*>/g, '')}
            </span>
          ) : null}

          <div className="note-editor-confirm-actions">
            <button
              type="button"
              className="note-editor-button ghost"
              onClick={() => this.setState({confirm: null})}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`note-editor-button ${destructive ? 'danger' : 'primary'}`}
              onClick={() => destructive
                ? this.deleteNote()
                : this.applyPreviousNote(confirm.note)}
            >
              {destructive ? 'Delete' : 'Replace'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const node = this.props.node;
    if (!node) return null;

    const image = node.image;
    const isImage = !!(image || node.iAm === 'image');
    const content = node.content || '';
    const previousNotes = this.sortedPreviousNotes();
    const context = this.contextLabel();

    return (
      <div
        id="edit-node"
        className={`report-modal note-editor${image ? ' has-image' : ''}${this.props.embedded ? ' is-embedded' : ''}`}
        onClick={(e) => this.props.embedded ? e.stopPropagation() : this.saveAndClose(e)}
      >
        <div className="note-editor-card" onClick={(e) => e.stopPropagation()}>
          <div className="note-editor-head">
            <div className="note-editor-heading">
              <span className="note-editor-title">{isImage ? 'Attachment' : 'Note'}</span>
              {context ? <span className="note-editor-context">{context}</span> : null}
            </div>

            <button
              type="button"
              className="note-editor-close"
              aria-label="Save and close"
              onClick={(e) => this.saveAndClose(e)}
            >
              <CloseRoundedIcon />
            </button>
          </div>

          {image ? (
            isPdfAttachment(node) ? (
              <div className="note-editor-image is-pdf">
                <PdfAttachment url={image} name={node.image_name} />
                <PdfPreviewFrame url={image} name={node.image_name} />
              </div>
            ) : (
              <div className="note-editor-image">
                <img src={image} className="file-upload-preview" alt="Attached" />
              </div>
            )
          ) : null}

          <div className="note-editor-body">
            <TextareaAutosize
              className="note-editor-input"
              placeholder={isImage ? 'Add a caption…' : 'Add a note…'}
              autoFocus
              minRows={isImage ? 2 : 3}
              maxRows={8}
              onKeyDown={e => this.handleKeyDown(e)}
              onChange={e => this.handleInputChange(e)}
              onFocus={function(e) {
                var val = e.target.value;
                e.target.value = '';
                e.target.value = val;
              }}
              value={content}
            />


          </div>

          {previousNotes.length ? this.renderPreviousNotes(previousNotes) : null}

          <div className="note-editor-foot">
            {previousNotes.length ? (
              <button
                type="button"
                className={`note-editor-reuse${this.state.previousOpen ? ' is-open' : ''}`}
                aria-expanded={this.state.previousOpen}
                onClick={() => this.setState({previousOpen: !this.state.previousOpen})}
              >
                <HistoryRoundedIcon className="note-editor-reuse-icon" />
                Previous
                <span className="note-editor-reuse-count">{previousNotes.length}</span>
                <ExpandMoreRoundedIcon className="note-editor-reuse-chevron" />
              </button>
            ) : <span />}

            <div className="note-editor-actions">
              <button
                type="button"
                className="note-editor-button ghost danger-text"
                aria-label="Delete note"
                onClick={(e) => {
                  e.stopPropagation();
                  this.requestDelete();
                }}
              >
                <DeleteOutlineRoundedIcon />
                Delete
              </button>

              <button
                type="button"
                className="note-editor-button primary"
                onClick={(e) => this.saveAndClose(e)}
              >
                <CheckRoundedIcon />
                Save
              </button>
            </div>
          </div>

          {this.state.confirm ? this.renderConfirm() : null}
        </div>
      </div>
    )
  }
}

function mapStateToProps(state, props) {
  return {
    authToken: state.authToken,
    report: state.report,
    tests: state.testFields
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(EditReportNote);
